import React, { useEffect, useRef, useMemo } from "react";
import { Box } from "@mui/material";
import * as echarts from "echarts";

const CategoricalBarChart = ({ chartData, height, tooltipCustomOption = undefined }) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  // 1. yAxis 階梯式跳躍邏輯
  const { dynamicMax, dynamicInterval } = useMemo(() => {
    if (!chartData?.seriesData || chartData.seriesData.length === 0) {
      return { dynamicMax: 50, dynamicInterval: 10 };
    }

    const values = chartData.seriesData.map((item) =>
      typeof item === "object" ? item.value : item
    );
    const maxValue = Math.max(...values);

    if (maxValue === 0) return { dynamicMax: 50, dynamicInterval: 10 };

    const finalMax = Math.ceil(maxValue / 10) * 10;

    return {
      dynamicMax: finalMax,
      dynamicInterval: finalMax / 5,
    };
  }, [chartData.seriesData]);

  useEffect(() => {
    const chartDom = chartRef.current;
    if (!chartDom) return;

    // 先檢查是否已經有實例存在，有的話先銷毀
    let chart = echarts.getInstanceByDom(chartDom);
    if (chart) {
      chart.dispose();
    }
    // 強制清空內容，防止 SVG 累積
    chartDom.innerHTML = '';

    chart = echarts.init(chartDom, null, { renderer: "svg" });
    chartInstance.current = chart;

    const option = {
      tooltip: tooltipCustomOption === null ? { show: false } : tooltipCustomOption,
      grid: {
        left: 20,
        right: 25,
        top: 20,
        bottom: 25,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: chartData.categories,
        axisLabel: { color: "#999", fontSize: 12, interval: 0 },
        axisLine: { lineStyle: { color: "#e0e0e0" } },
        axisTick: { show: false },
        splitLine: { show: false }, // 移除垂直格線以符合截圖
      },
      yAxis: {
        type: "value",
        min: 0,
        max: dynamicMax,
        interval: dynamicInterval,
        axisLabel: {
          color: "#999",
          formatter: (value) => value.toFixed(0)
        },
        axisLine: { show: false },
        splitLine: { show: true, lineStyle: { type: "dotted", color: "#e0e0e0" } },
      },
      series: [
        {
          name: "Series",
          type: "bar",
          data: chartData.seriesData,
          barWidth: "30%",
          itemStyle: {
            borderRadius: [0, 0, 0, 0],
          },
        },
      ],
    };

    chart.setOption(option);

    const resizeObserver = new ResizeObserver(() => chart.resize());
    resizeObserver.observe(chartDom);

    return () => {
      resizeObserver.disconnect();
      chart.dispose();
    };
  }, [chartData, tooltipCustomOption, dynamicMax, dynamicInterval]);


  return (
    <Box
      ref={chartRef}
      style={{
        width: "100%",
        height,
        background: "#fff",
        borderRadius: "8px"
      }}
    />
  );
};

export default CategoricalBarChart;